import type { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import type { ConstructionTypeData } from 'gql/graphql';
import { ProjectMethodologyEnum } from 'gql/graphql';

import { useMemo } from 'react';

import { bimContractConstructionTypesGql, uerContractConstructionTypesGql } from '../contractQueries';

export const useContractConstructionTypes = (
    contractId: number | undefined,
    methodology?: ProjectMethodologyEnum,
    withEffect = true,
): [ConstructionTypeData[], RequestStatus, any] => {
    const requestVariables = useMemo(
        () => ({
            contractId,
        }),
        [contractId],
    );

    const isUer = useMemo(() => methodology === ProjectMethodologyEnum.Uer, [methodology]);

    const [contractConstructionTypesData, contractConstructionTypesStatus, contractConstructionTypesError] = useRequest(
        {
            requestId: 'contractConstructionTypes',
            query: uerContractConstructionTypesGql,
            withEffect: withEffect && isUer && Boolean(contractId),
            variables: requestVariables,
        },
    );

    const [bimContractConstructionTypesData, bimContractConstructionTypesStatus, bimContractConstructionTypesError] =
        useRequest({
            requestId: 'contractConstructionTypes',
            query: bimContractConstructionTypesGql,
            withEffect: withEffect && !isUer && Boolean(contractId),
            variables: requestVariables,
        });

    const data = useMemo(
        () =>
            (isUer
                ? contractConstructionTypesData?.uerContractConstructionTypes?.data?.filter((i) => !!i) || []
                : bimContractConstructionTypesData?.bimContractConstructionTypes?.data?.filter((i) => !!i) ||
                  []) as ConstructionTypeData[],
        [
            bimContractConstructionTypesData?.bimContractConstructionTypes?.data,
            contractConstructionTypesData?.uerContractConstructionTypes?.data,
            isUer,
        ],
    );

    const status = useMemo(
        () => (isUer ? contractConstructionTypesStatus : bimContractConstructionTypesStatus),
        [bimContractConstructionTypesStatus, contractConstructionTypesStatus, isUer],
    );

    const error = useMemo(
        () => (isUer ? contractConstructionTypesError : bimContractConstructionTypesError),
        [bimContractConstructionTypesError, contractConstructionTypesError, isUer],
    );

    return [data, status, error];
};
